const DEFAULT_FILL = 'rgba(100, 116, 139, 0.18)'

function finiteNumber(value) {
  return typeof value === 'number' && Number.isFinite(value)
}

function barInstant(bar) {
  if (finiteNumber(bar?.rawEpochMs)) return bar.rawEpochMs
  if (finiteNumber(bar?.time)) return bar.time * 1000
  return null
}

/**
 * Build contiguous pre/post-market spans from ordered chart candles.
 * `getSession` is injected so the chart's calendar policy stays outside the
 * renderer and this helper remains deterministic in tests.
 */
export function buildSessionBackgroundSpans(bars, getSession) {
  if (!Array.isArray(bars) || typeof getSession !== 'function') return []

  const spans = []
  let active = null
  for (const bar of bars) {
    const time = bar?.time
    const instant = barInstant(bar)
    const session = instant == null ? 'closed' : getSession(new Date(instant))
    const extended = session === 'pre' || session === 'post'
    if (!extended || !finiteNumber(time)) {
      if (active) spans.push(active)
      active = null
      continue
    }

    if (!active || active.session !== session) {
      if (active) spans.push(active)
      active = { startTime: time, endTime: time, session }
    } else {
      active.endTime = time
    }
  }
  if (active) spans.push(active)
  return spans
}

export class SessionBackgroundPrimitive {
  constructor() {
    this.__isSessionBackgroundPrimitive = true
    this._data = { spans: [], fillStyle: DEFAULT_FILL }
    this._chart = null
    this._requestUpdate = null
    this._paneViews = [new SessionBackgroundPaneView(this)]
  }

  attached(param) {
    this._chart = param.chart
    this._requestUpdate = param.requestUpdate
  }

  detached() {
    this._chart = null
    this._requestUpdate = null
    this._data = { spans: [], fillStyle: DEFAULT_FILL }
  }

  update(next) {
    this._data = {
      spans: Array.isArray(next?.spans) ? next.spans : [],
      fillStyle: next?.fillStyle || DEFAULT_FILL,
    }
    if (typeof this._requestUpdate === 'function') this._requestUpdate()
  }

  updateAllViews() {}

  paneViews() {
    return this._paneViews
  }
}

class SessionBackgroundPaneView {
  constructor(primitive) {
    this._renderer = new SessionBackgroundPaneRenderer(primitive)
  }

  zOrder() { return 'bottom' }

  renderer() { return this._renderer }
}

class SessionBackgroundPaneRenderer {
  constructor(primitive) {
    this._primitive = primitive
  }

  draw() {}

  drawBackground(target) {
    const { spans, fillStyle } = this._primitive._data
    const chart = this._primitive._chart
    if (!spans.length || !chart) return

    target.useBitmapCoordinateSpace((scope) => {
      const ctx = scope.context
      const timeScale = chart.timeScale()
      const ratio = scope.horizontalPixelRatio || 1
      const spacing = Number(timeScale?.options?.()?.barSpacing)
      const halfSpacing = (Number.isFinite(spacing) && spacing > 0 ? spacing : 6) * ratio / 2
      const height = scope.bitmapSize?.height || scope.context?.canvas?.height || 0
      if (!height) return

      ctx.save()
      ctx.fillStyle = fillStyle
      for (const span of spans) {
        const start = timeScale.timeToCoordinate(span.startTime)
        const end = timeScale.timeToCoordinate(span.endTime)
        if (start == null && end == null) continue
        const x1 = (start == null ? end : start) * ratio - halfSpacing
        const x2 = (end == null ? start : end) * ratio + halfSpacing
        ctx.fillRect(Math.min(x1, x2), 0, Math.max(1, Math.abs(x2 - x1)), height)
      }
      ctx.restore()
    })
  }
}

